import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';

const DEFAULT_PREFS = {
  emailProjectUpdates: true,
  emailPermitAlerts: true,
  emailTeamActivity: false,
  emailWeeklyDigest: true,
  inAppProjectUpdates: true,
  inAppPermitAlerts: true,
  inAppTeamActivity: true,
  inAppNewMessages: true
};

const NotificationsTab = () => {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadPrefs();
  }, [user]);

  const loadPrefs = async () => {
    if (!user) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase?.from('user_profiles')?.select('notification_preferences')?.eq('id', user?.id)?.maybeSingle();

      if (!fetchError && data?.notification_preferences) {
        setPrefs({ ...DEFAULT_PREFS, ...data?.notification_preferences });
      }
    } catch (err) {
      console.error('Load notification prefs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { error: saveError } = await supabase?.from('user_profiles')?.update({
          notification_preferences: prefs,
          updated_at: new Date()?.toISOString()
        })?.eq('id', user?.id);

      if (saveError) throw saveError;
      setSuccess('Notification preferences saved.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      console.error('Save notification prefs error:', err);
      setError('Failed to save preferences. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const emailNotifications = [
    { key: 'emailProjectUpdates', label: 'Project Updates', description: 'Get notified when project status changes' },
    { key: 'emailPermitAlerts', label: 'Permit Alerts', description: 'Receive alerts for permit deadlines and approvals' },
    { key: 'emailTeamActivity', label: 'Team Activity', description: 'Notifications for team member actions' },
    { key: 'emailWeeklyDigest', label: 'Weekly Digest', description: 'A weekly summary of activity in your organization' }
  ];

  const inAppNotifications = [
    { key: 'inAppProjectUpdates', label: 'Project Updates', description: 'In-app alerts for project status changes' },
    { key: 'inAppPermitAlerts', label: 'Permit Alerts', description: 'In-app alerts for permit deadlines' },
    { key: 'inAppTeamActivity', label: 'Team Activity', description: 'In-app notifications for team actions' },
    { key: 'inAppNewMessages', label: 'New Messages', description: 'Alerts for new messages and mentions' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading preferences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Email Notifications */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="Mail" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">Email Notifications</h3>
            <p className="text-sm text-muted-foreground">Choose which emails you'd like to receive</p>
          </div>
        </div>
        <div className="space-y-4">
          {emailNotifications?.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={prefs?.[key]}
                  onChange={() => handleToggle(key)}
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
      {/* In-App Notifications */}
      <div className="bg-card rounded-xl p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Bell" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">In-App Notifications</h3>
            <p className="text-sm text-muted-foreground">Control what appears in your notification center</p>
          </div>
        </div>
        <div className="space-y-4">
          {inAppNotifications?.map(({ key, label, description }) => (
            <div key={key} className="flex items-center justify-between p-4 border border-border rounded-lg">
              <div>
                <p className="text-sm font-medium text-foreground">{label}</p>
                <p className="text-xs text-muted-foreground">{description}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="sr-only peer"
                  checked={prefs?.[key]}
                  onChange={() => handleToggle(key)}
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-ring rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          ))}
        </div>
      </div>
      {/* Feedback */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-error/10 border border-error/20 rounded-lg text-sm text-error">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center gap-2 p-3 bg-success/10 border border-success/20 rounded-lg text-sm text-success">
          <Icon name="CheckCircle" size={16} />
          <span>{success}</span>
        </div>
      )}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <><Icon name="Loader2" size={18} className="animate-spin" /><span>Saving...</span></>
          ) : (
            <><Icon name="Save" size={18} /><span>Save Preferences</span></>
          )}
        </Button>
      </div>
    </div>
  );
};

export default NotificationsTab;
